/**
 * The right-click menu: one for the whole app, replacing the browser's own (which offers
 * "Inspect" and other things that make no sense here).
 *
 * Any window can open it with its own entries from its `contextmenu` handler
 * ({@link ContextMenu.open}). When nobody did, the shell opens the general one
 * ({@link ContextMenu.general}), so a right click never shows the browser's menu. The shell
 * also renders it (`app/ContextMenu.tsx`).
 *
 * Entries are plain data: a label and what to do, either a function or a named command (whose
 * keyboard shortcut is then shown beside it, from the config).
 */
import { createSignal, type Accessor } from "solid-js";
import { tidyEntries, type MenuEntry } from "../shared/menus";

export { SEPARATOR, isItem, type MenuEntry, type MenuItem } from "../shared/menus";

/** The open menu: where and what. */
export interface MenuState {
  /** Page coordinates of the click. */
  x: number;
  y: number;
  entries: MenuEntry[];
  /** The menu bar's menu it is (`file`...), `null` for a right-click menu. */
  owner: string | null;
}

/** The menu service. */
export interface ContextMenu {
  /** The open menu, or `null` (reactive). */
  state: Accessor<MenuState | null>;
  /** Opens the menu at the event's position with `entries`, and marks the event as handled. */
  open(event: MouseEvent, entries: MenuEntry[]): void;
  /** Opens the menu at a page position (keyboard menu key, tests), or below a menu bar title (`owner`). */
  openAt(x: number, y: number, entries: MenuEntry[], owner?: string): void;
  /** Closes it. */
  close(): void;
  /** `true` if a window already opened a menu for this event. */
  handled(event: Event): boolean;
  /** The general entries (undo, save, windows, theme...), set once by the shell. */
  general(): MenuEntry[];
  /** Sets where {@link general} comes from. */
  setGeneral(entries: () => MenuEntry[]): void;
  /** Opens the menu bar's menu `step` places from the open one (left/right arrows); nothing for a right-click menu. */
  step(step: number): void;
  /** Sets how the menu bar opens its neighbouring menus (`app/MenuBar.tsx`). */
  setNeighbours(open: (owner: string, step: number) => void): void;
}

/** Creates the service (closed). */
export function createContextMenu(): ContextMenu {
  const [state, setState] = createSignal<MenuState | null>(null);
  const opened = new WeakSet<Event>();
  let general: () => MenuEntry[] = () => [];
  let neighbours: (owner: string, step: number) => void = () => {};
  /** Drops stray separators and empty headings, so entries can be built carelessly. */
  const tidy = tidyEntries;
  return {
    state,
    open(event, entries) {
      event.preventDefault();
      opened.add(event);
      setState({ x: event.clientX, y: event.clientY, entries: tidy(entries), owner: null });
    },
    openAt(x, y, entries, owner) {
      setState({ x, y, entries: tidy(entries), owner: owner ?? null });
    },
    close: () => setState(null),
    handled: (event) => opened.has(event),
    general: () => general(),
    setGeneral(entries) {
      general = entries;
    },
    step(step) {
      const owner = state()?.owner;
      if (owner) neighbours(owner, step);
    },
    setNeighbours(open) {
      neighbours = open;
    },
  };
}
